import { DateService } from "@/core/services/Date.service";
import {
  IPromoDetail,
  IPromoDetailLifecycle,
  IPromoMatchStatusCount,
} from "@/core/repositories/promotion/IPromoDetail.repository";
import { IPromoFinancials, IPromoStatusInfo, IPromoTypeInfo } from "@/core/repositories/promotion";
import { DateAdapter } from "@/infrastructure/adapters/dayJs/DayJs.adapter";
import { NumberFormatterService } from "@/core/services/NumberFormatter.service";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number/NumberFormatter.adapter";
import { NumberFormatType } from "@/core/types";
import { PromotionSourceDocument } from "@/core/entities/promotion";

export type PromoDetailProps = IPromoDetail;

const END_DATE_NEAR_DAYS = 14;

export class PromoDetail implements IPromoDetail {
  private readonly _promoEventId: number;
  private readonly _promoName: string;
  private readonly _promoCode: string | null;
  private readonly _type: IPromoTypeInfo;
  private readonly _status: IPromoStatusInfo;
  private readonly _purchaser: IPromoDetail["purchaser"];
  private readonly _retailer: IPromoDetail["retailer"];
  private readonly _fundingMethod: string | null;
  private readonly _startDate: string;
  private readonly _endDate: string;
  private readonly _amountTolerance: string | null;
  private readonly _dateTolerance: string | null;
  private readonly _notes: string | null;
  private readonly _financials: IPromoFinancials;
  private readonly _lifecycle: IPromoDetailLifecycle;
  private readonly _matchBreakdown: IPromoMatchStatusCount[];
  private readonly _productsInScopeCount: number;
  private readonly _matchedDeductionsCount: number;
  private readonly _appliedDeductionsCount: number;
  private readonly _isCurrentlyActive: boolean;
  private readonly _sourceDocument: PromotionSourceDocument | null;
  private readonly _dateService: DateService;
  private readonly _numberService: NumberFormatterService;


  constructor(props: PromoDetailProps) {
    this._promoEventId = props.promoEventId;
    this._promoName = props.promoName;
    this._promoCode = props.promoCode;
    this._type = props.type;
    this._status = props.status;
    this._purchaser = props.purchaser;
    this._retailer = props.retailer;
    this._fundingMethod = props.fundingMethod;
    this._startDate = props.startDate;
    this._endDate = props.endDate;
    this._amountTolerance = props.amountTolerance;
    this._dateTolerance = props.dateTolerance;
    this._notes = props.notes;
    this._financials = props.financials;
    this._lifecycle = props.lifecycle;
    this._matchBreakdown = props.matchBreakdown;
    this._productsInScopeCount = props.productsInScopeCount;
    this._matchedDeductionsCount = props.matchedDeductionsCount;
    this._appliedDeductionsCount = props.appliedDeductionsCount;
    this._isCurrentlyActive = props.isCurrentlyActive;
    this._dateService = new DateService(new DateAdapter());
    this._numberService = new NumberFormatterService(new NumberFormatterAdapter());
    this._sourceDocument = props.sourceDocument ? new PromotionSourceDocument(props.sourceDocument) : null;
  }

  public get promoEventId(): number {
    return this._promoEventId;
  }

  public get promoName(): string {
    return this._promoName;
  }

  public get promoCode(): string | null {
    return this._promoCode;
  }

  public get type(): IPromoTypeInfo {
    return this._type;
  }

  public get status(): IPromoStatusInfo {
    return this._status;
  }

  public get purchaser(): IPromoDetail["purchaser"] {
    return this._purchaser;
  }

  public get retailer(): IPromoDetail["retailer"] {
    return this._retailer;
  }

  public get fundingMethod(): string | null {
    return this._fundingMethod;
  }

  public get startDate(): string {
    return this._startDate;
  }

  public get endDate(): string {
    return this._endDate;
  }

  public get amountTolerance(): string | null {
    return this._amountTolerance;
  }

  public get dateTolerance(): string | null {
    return this._dateTolerance;
  }

  public get notes(): string | null {
    return this._notes;
  }

  public get financials(): IPromoFinancials {
    return this._financials;
  }

  public get lifecycle(): IPromoDetailLifecycle {
    return this._lifecycle;
  }

  public get matchBreakdown(): IPromoMatchStatusCount[] {
    return this._matchBreakdown;
  }

  public get productsInScopeCount(): number {
    return this._productsInScopeCount;
  }

  public get matchedDeductionsCount(): number {
    return this._matchedDeductionsCount;
  }

  public get appliedDeductionsCount(): number {
    return this._appliedDeductionsCount;
  }

  public get isCurrentlyActive(): boolean {
    return this._isCurrentlyActive;
  }

  public isDraft(): boolean {
    return this._status.promoStatusKey === "DRAFT";
  }

  public isActiveStatus(): boolean {
    return (
      this._isCurrentlyActive ||
      this._status.promoStatusKey === "ACTIVE" ||
      this._status.promoStatusKey === "APPROVED"
    );
  }

  public isCompleted(): boolean {
    return this._status.promoStatusKey === "COMPLETED";
  }

  public isCancelled(): boolean {
    return this._status.promoStatusKey === "CANCELLED";
  }

  public isEndDateNear(): boolean {
    if (!this._endDate || this.isEndDatePast()) {
      return false;
    }
    const today = this._dateService.getCurrentDate().toISOString();
    const daysUntilEnd = this._dateService.diff(this._endDate, today, "day");
    return daysUntilEnd >= 0 && daysUntilEnd <= END_DATE_NEAR_DAYS;
  }

  public isEndDatePast(): boolean {
    if (!this._endDate) {
      return false;
    }
    const today = this._dateService.getCurrentDate().toISOString();
    return this._dateService.isAfter(today, this._endDate);
  }

  public lifecycleNote(): string | null {
    if (this.isDraft()) {
      return "This promotion is in Draft and is not yet active in the matching engine. Activate it to start matching incoming deductions.";
    }
    if (this.isCompleted() || this.isCancelled()) {
      return null;
    }
    if (this.isActiveStatus() && this.isEndDatePast()) {
      return "This promotion's end date has passed. Review matched deductions and mark as Completed when done.";
    }
    if (this.isActiveStatus()) {
      return "Completing this promotion stops new deductions from being automatically matched to it. Existing confirmed matches are not affected.";
    }
    return null;
  }

  public primaryActionLabel(): string | null {
    if (this.isDraft()) {
      return "Activate Promotion";
    }
    if (this.isCancelled()) {
      return "Re-Activate";
    }
    if (this.isActiveStatus() && !this.isCompleted() && !this.isCancelled()) {
      return "Mark as Completed";
    }
    return null;
  }

  public canCancelProgram(): boolean {
    return !this.isDraft() && this.isActiveStatus();
  }

  public canDeleteProgram(): boolean {
    return this.isDraft();
  }

  public canEditProgram(): boolean {
    return !this.isCompleted() && !this.isCancelled();
  }

  public canEditProgramDetails(): boolean {
    return this.isDraft();
  }

  public formatDate(date: string | null | undefined, format: string): string {
    if (!date) {
      return "-";
    }
    return this._dateService.format(date, format);
  }

  public formatNumber(number: number | null | undefined, format: string): string {
    if (number == null) {
      return "-";
    }
    return this._numberService.format(number, format as NumberFormatType);
  }

  public displayFundingMethod(): string {
    return this._fundingMethod ?? "-";
  }

  public get sourceDocument(): PromotionSourceDocument | null {
    return this._sourceDocument;
  }
}
